import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import heic2any from 'heic2any';
import { storage } from '../../firebase';
import { useAuth } from '../../components/AuthContext';
import {
  disconnectMemberCalendar,
  getMemberCalendarConnection,
  startMemberCalendarOAuth,
  syncMyMemberCalendarEvents,
} from '../../services/calendarConnectionApi';
import { getMyTeamProfile, saveMyTeamProfile } from '../../services/teamProfileApi';

const ROLE_LABELS = {
  sales: 'Ventas',
  services: 'Servicios',
  installations: 'Instalaciones',
  demos: 'Demostraciones',
  seller: 'Ventas',
  admin: 'Administrador',
};

const initialForm = {
  displayName: '',
  title: '',
  bio: '',
  highlightsText: '',
  solutionsText: '',
  whatsapp: '',
  instagram: '',
  facebook: '',
  linkedin: '',
  website: '',
  publicEmail: '',
  publicPhone: '',
  mainPhone: '',
};

const MAX_PROMO_SIZE_MB = 25;
const MAX_PROFILE_IMAGE_SIZE_MB = 25;
const emptyPromotion = { title: '', imageUrl: '', link: '', mediaUrl: '', mediaType: '' };

const uploadErrorMessage = (error) => {
  const code = String(error?.code || '');
  if (code === 'storage/unauthorized') return 'Tu sesión no tiene permiso para subir la imagen. Cierra sesión y vuelve a entrar.';
  if (code === 'storage/canceled') return 'La subida fue cancelada.';
  if (code === 'storage/quota-exceeded') return 'No hay espacio disponible para subir la imagen.';
  if (code === 'storage/retry-limit-exceeded') return 'La conexión tardó demasiado. Intenta otra vez con Wi‑Fi o una foto más pequeña.';
  if (code === 'storage/unknown') return 'El teléfono no pudo completar la subida. Intenta nuevamente con una foto JPG o PNG.';
  return 'No se pudo subir la imagen. Intenta nuevamente.';
};

const isHeicPhoto = (file) => {
  const type = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  return type === 'image/heic' || type === 'image/heif' || /\.(heic|heif)$/.test(name);
};

const makeJpegFromHeic = async (file) => {
  const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
  const blob = Array.isArray(converted) ? converted[0] : converted;
  return new File([blob], `perfil-${Date.now()}.jpg`, { type: 'image/jpeg' });
};

const StaffProfile = ({ embedded = false }) => {
  const auth = getAuth();
  const { userRole } = useAuth();
  const [user, setUser] = useState(auth.currentUser);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [imageUpload, setImageUpload] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [roleLabel, setRoleLabel] = useState('');
  const [roleValue, setRoleValue] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const [promotions, setPromotions] = useState([emptyPromotion]);
  const [promoUploadIndex, setPromoUploadIndex] = useState(null);
  const [calendarConnection, setCalendarConnection] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarConnecting, setCalendarConnecting] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
    });
    return unsubscribe;
  }, [auth]);

  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.uid) return;
      setLoading(true);
      try {
        const snapshot = await getMyTeamProfile();
        const userData = snapshot?.user || {};
        const role = userData?.role || '';
        const label = userData?.roleLabel || ROLE_LABELS[role] || 'Equipo';
        setRoleValue(role);
        setRoleLabel(label);

        const profileData = snapshot?.profile || {};

        setForm({
          displayName: profileData.displayName || userData.name || user.displayName || '',
          title: profileData.title || '',
          bio: profileData.bio || '',
          highlightsText: Array.isArray(profileData.highlights)
            ? profileData.highlights.join('\n')
            : '',
          solutionsText: Array.isArray(profileData.solutions)
            ? profileData.solutions.join('\n')
            : '',
          whatsapp: profileData.socials?.whatsapp || '',
          instagram: profileData.socials?.instagram || '',
          facebook: profileData.socials?.facebook || '',
          linkedin: profileData.socials?.linkedin || '',
          website: profileData.socials?.website || '',
          publicEmail: profileData.publicContact?.email || userData.email || user.email || '',
          publicPhone: profileData.publicContact?.phone || '',
          mainPhone: profileData.publicContact?.mainPhone || '',
        });

        setImageUrl(profileData.imageUrl || '');
        setPromotions(
          Array.isArray(profileData.promotions) && profileData.promotions.length
            ? profileData.promotions.map((promo) => ({
                title: promo.title || '',
                imageUrl: promo.imageUrl || '',
                link: promo.link || '',
                mediaUrl: promo.mediaUrl || '',
                mediaType: promo.mediaType || '',
              }))
            : [emptyPromotion]
        );
      } catch (err) {
        console.error('❌ Error cargando perfil de equipo:', err);
        setStatus({ type: 'error', message: 'No se pudo cargar el perfil.' });
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  const loadCalendarConnection = async () => {
    if (!user?.uid) return;
    setCalendarLoading(true);
    try {
      const connection = await getMemberCalendarConnection();
      setCalendarConnection(connection);
    } catch (err) {
      console.error('❌ Error cargando conexión de calendario:', err);
    } finally {
      setCalendarLoading(false);
    }
  };

  useEffect(() => {
    loadCalendarConnection();
    // La función lee la sesión actual; se recarga solo al cambiar de usuario.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  useEffect(() => {
    const result = new URLSearchParams(window.location.search).get('calendar');
    if (!result) return;
    const messages = {
      connected: { type: 'success', message: 'Tu calendario Google quedó conectado para consultar disponibilidad.' },
      expired: { type: 'error', message: 'La conexión expiró. Intenta conectar el calendario otra vez.' },
      error: { type: 'error', message: 'No se pudo conectar Google Calendar. Intenta nuevamente.' },
    };
    if (messages[result]) setStatus(messages[result]);
    window.history.replaceState({}, document.title, window.location.pathname);
    loadCalendarConnection();
    // Solo procesa el retorno OAuth al cargar la vista.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnectCalendar = async () => {
    setCalendarConnecting(true);
    try {
      const { authorizationUrl } = await startMemberCalendarOAuth();
      window.location.assign(authorizationUrl);
    } catch (err) {
      console.error('❌ Error iniciando OAuth de calendario:', err);
      setStatus({ type: 'error', message: 'No pudimos iniciar la conexión con Google Calendar.' });
      setCalendarConnecting(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    setCalendarConnecting(true);
    try {
      await disconnectMemberCalendar();
      setCalendarConnection({ status: 'disconnected' });
      setStatus({ type: 'success', message: 'Tu calendario personal fue desconectado.' });
    } catch (err) {
      console.error('❌ Error desconectando calendario:', err);
      setStatus({ type: 'error', message: 'No pudimos desconectar el calendario.' });
    } finally {
      setCalendarConnecting(false);
    }
  };

  const handleSyncCalendar = async () => {
    setCalendarConnecting(true);
    try {
      const result = await syncMyMemberCalendarEvents();
      setStatus({ type: 'success', message: `${result.synced || 0} evento(s) asignado(s) fueron sincronizados con tu Google Calendar.${result.failed ? ` ${result.failed} requieren revisión.` : ''}` });
      await loadCalendarConnection();
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'No se pudo sincronizar el calendario.' });
    } finally {
      setCalendarConnecting(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = async (event) => {
    let file = event.target.files?.[0];
    // iPhone a veces no vuelve a disparar onChange si se elige la misma foto.
    // Limpiamos el selector tras guardarla en estado para permitir reintentos.
    event.target.value = '';
    if (!file) return;
    if (file.type && !file.type.startsWith('image/')) {
      setImageUpload(null);
      setStatus({ type: 'error', message: 'Selecciona una imagen desde Fotos o Cámara.' });
      return;
    }
    if (file.size > MAX_PROFILE_IMAGE_SIZE_MB * 1024 * 1024) {
      setImageUpload(null);
      setStatus({ type: 'error', message: `La foto supera ${MAX_PROFILE_IMAGE_SIZE_MB}MB. Elige una más pequeña.` });
      return;
    }
    if (isHeicPhoto(file)) {
      setStatus({ type: 'success', message: 'Preparando la foto del iPhone para subirla...' });
      try {
        file = await makeJpegFromHeic(file);
      } catch (error) {
        console.error('❌ Error convirtiendo HEIC:', error);
        setImageUpload(null);
        setStatus({ type: 'error', message: 'No pudimos preparar esta foto HEIC. Intenta tomar otra foto o selecciona una en formato JPG.' });
        return;
      }
    }
    setImageUpload(file);
    setStatus({ type: 'success', message: `Foto lista: ${file.name || 'imagen seleccionada'}. Toca “Subir imagen”.` });
  };

  const handleImageUpload = async () => {
    if (!imageUpload || !user?.uid) return;
    setUploading(true);
    setStatus(null);
    try {
      // Conservamos una extensión coherente con el archivo. Un HEIC guardado
      // como .jpg puede aparecer como “?” en Safari/iPhone aunque la subida
      // haya terminado correctamente.
      const extensionByType = {
        'image/jpeg': 'jpg',
        'image/png': 'png',
        'image/webp': 'webp',
        'image/heic': 'heic',
        'image/heif': 'heif',
      };
      const originalExtension = String(imageUpload.name || '').split('.').pop().toLowerCase();
      const extension = extensionByType[imageUpload.type] || (originalExtension && originalExtension.length <= 5 ? originalExtension : 'jpg');
      const imgRef = storageRef(storage, `team/${user.uid}/profile-${Date.now()}.${extension}`);
      // Algunos iPhone no informan MIME al escoger desde Fotos. Forzamos un
      // tipo seguro para que Storage no rechace una imagen válida.
      const contentType = imageUpload.type?.startsWith('image/') ? imageUpload.type : 'image/jpeg';
      await uploadBytes(imgRef, imageUpload, {
        contentType,
        cacheControl: 'public,max-age=300',
      });
      const url = await getDownloadURL(imgRef);
      // El archivo conserva el mismo nombre. Esta versión evita que la tarjeta
      // NFC o el navegador sigan mostrando una foto anterior desde caché.
      const publicUrl = `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`;
      setImageUrl(publicUrl);
      setImageUpload(null);
      setStatus({
        type: 'success',
        message: 'Imagen subida. Guarda el perfil para publicarla en tu tarjeta NFC.',
      });
    } catch (err) {
      console.error('❌ Error subiendo imagen:', err);
      setStatus({ type: 'error', message: uploadErrorMessage(err) });
    } finally {
      setUploading(false);
    }
  };

  const handlePromotionChange = (index, field, value) => {
    setPromotions((prev) =>
      prev.map((promo, idx) =>
        idx === index ? { ...promo, [field]: value } : promo
      )
    );
  };

  const handlePromotionFile = async (index, file) => {
    if (!file || !user?.uid) return;
    if (file.size > MAX_PROMO_SIZE_MB * 1024 * 1024) {
      setStatus({
        type: 'error',
        message: `El archivo supera ${MAX_PROMO_SIZE_MB}MB.`,
      });
      return;
    }
    setPromoUploadIndex(index);
    setStatus(null);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const promoRef = storageRef(
        storage,
        `team/${user.uid}/promos/${Date.now()}_${safeName}`
      );
      await uploadBytes(promoRef, file);
      const url = await getDownloadURL(promoRef);
      handlePromotionChange(index, 'mediaUrl', url);
      handlePromotionChange(index, 'mediaType', file.type || '');
      setStatus({ type: 'success', message: 'Promocion subida.' });
    } catch (err) {
      console.error('❌ Error subiendo promocion:', err);
      setStatus({ type: 'error', message: 'No se pudo subir la promocion.' });
    } finally {
      setPromoUploadIndex(null);
    }
  };

  const addPromotion = () => {
    setPromotions((prev) => [...prev, { ...emptyPromotion }]);
  };

  const removePromotion = (index) => {
    setPromotions((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSave = async () => {
    if (!user?.uid) return;
    setSaving(true);
    setStatus(null);
    try {
      const highlights = form.highlightsText
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean);
      const solutions = form.solutionsText
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean);

      const payload = {
        uid: user.uid,
        email: user.email || '',
        displayName: form.displayName.trim(),
        title: form.title.trim(),
        bio: form.bio.trim(),
        role: roleValue,
        roleLabel,
        imageUrl: imageUrl || '',
        highlights,
        solutions,
        socials: {
          whatsapp: form.whatsapp.trim(),
          instagram: form.instagram.trim(),
          facebook: form.facebook.trim(),
          linkedin: form.linkedin.trim(),
          website: form.website.trim(),
        },
        publicContact: {
          email: form.publicEmail.trim(),
          phone: form.publicPhone.trim(),
          mainPhone: form.mainPhone.trim(),
        },
        promotions: promotions
          .map((promo) => ({
            title: promo.title.trim(),
            imageUrl: promo.imageUrl.trim(),
            mediaUrl: promo.mediaUrl?.trim() || '',
            mediaType: promo.mediaType?.trim() || '',
            link: promo.link.trim(),
          }))
          .filter((promo) => promo.title || promo.imageUrl || promo.mediaUrl || promo.link),
      };
      await saveMyTeamProfile(payload);
      setStatus({ type: 'success', message: 'Perfil guardado correctamente.' });
    } catch (err) {
      console.error('❌ Error guardando perfil:', err);
      setStatus({ type: 'error', message: 'No se pudo guardar el perfil.' });
    } finally {
      setSaving(false);
    }
  };

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const nfcLink = user?.uid && origin ? `${origin}/nfc/team/${user.uid}` : '';

  const handleCopyLink = async () => {
    if (!nfcLink) return;
    try {
      await navigator.clipboard.writeText(nfcLink);
      setCopyStatus('Link copiado.');
    } catch (err) {
      console.error('❌ Error copiando link NFC:', err);
      setCopyStatus('No se pudo copiar el link.');
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500">Cargando perfil...</p>;
  }

  const backLink =
    userRole === 'admin'
      ? { to: '/admin/dashboard', label: 'Volver al panel' }
      : userRole === 'seller'
      ? { to: '/seller', label: 'Volver a ventas' }
      : { to: '/', label: 'Ir al inicio' };

  const profileRoute = userRole === 'admin' ? '/admin/profile' : '/staff/profile';

  return (
    <div className="space-y-6">
      {!embedded && <div className="rounded-xl bg-white p-4 shadow-sm flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-gray-500">
          Navegación rápida
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to={backLink.to}
            className="rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {backLink.label}
          </Link>
          <Link
            to={profileRoute}
            className="rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-medium text-orange-700"
          >
            Mi perfil
          </Link>
        </div>
      </div>}

      <div className="rounded-xl bg-white p-6 shadow-sm">
        <h2 className="text-2xl font-semibold text-gray-900">Mi perfil</h2>
        <p className="text-gray-500">
          Completa tu perfil para mostrar lo mejor de nuestras soluciones.
        </p>
        <p className="mt-2 text-sm text-orange-600 font-semibold">{roleLabel}</p>
      </div>

      {status && (
        <div
          className={`rounded-lg border px-4 py-2 ${
            status.type === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          {status.message}
        </div>
      )}

      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Mi Google Calendar</h3>
            <p className="mt-1 text-sm text-gray-500">
              BBTS consulta tus bloques libre/ocupado para evitar choques y copia a este calendario las citas que te asignen. No lee títulos ni notas personales.
            </p>
            {!calendarLoading && calendarConnection?.status === 'connected' && (
              <p className="mt-2 text-sm font-medium text-emerald-700">
                {calendarConnection.mode === 'personal_sync' ? 'Sincronización activa' : 'Conexión antigua'}{calendarConnection.googleEmail ? `: ${calendarConnection.googleEmail}` : ''}
              </p>
            )}
            {!calendarLoading && calendarConnection?.status !== 'connected' && (
              <p className="mt-2 text-sm text-gray-500">No conectado.</p>
            )}
          </div>
          {calendarConnection?.status === 'connected' ? (
            <div className="flex flex-wrap gap-2">
              {calendarConnection.mode === 'personal_sync' ? <button type="button" onClick={handleSyncCalendar} disabled={calendarConnecting} className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-60">{calendarConnecting ? 'Sincronizando...' : 'Sincronizar mis citas'}</button> : <button type="button" onClick={handleConnectCalendar} disabled={calendarConnecting} className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-60">{calendarConnecting ? 'Abriendo Google...' : 'Actualizar conexión'}</button>}
              <button
                type="button"
                onClick={handleDisconnectCalendar}
                disabled={calendarConnecting}
                className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
              >
                Desconectar
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleConnectCalendar}
              disabled={calendarLoading || calendarConnecting}
              className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-60"
            >
              {calendarConnecting ? 'Abriendo Google...' : 'Conectar mi calendario'}
            </button>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Datos visibles en mi firma de correo</h3>
        <p className="mb-4 text-sm text-gray-500">Estos son los únicos datos de contacto que BBTS puede incluir cuando escribes a un cliente.</p>
        <div className="grid gap-4 md:grid-cols-3">
          <input
            type="email"
            name="publicEmail"
            placeholder="Correo público"
            value={form.publicEmail}
            onChange={handleChange}
            className="rounded-lg border border-gray-300 px-3 py-2"
          />
          <input
            type="text"
            name="publicPhone"
            placeholder="Teléfono directo"
            value={form.publicPhone}
            onChange={handleChange}
            className="rounded-lg border border-gray-300 px-3 py-2"
          />
          <input
            type="text"
            name="mainPhone"
            placeholder="Teléfono oficina"
            value={form.mainPhone}
            onChange={handleChange}
            className="rounded-lg border border-gray-300 px-3 py-2"
          />
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Imagen de perfil</h3>
        <div className="flex flex-col items-center gap-4">
          <img
            src={imageUrl || '/logo-bbts-round.png'}
            alt="Foto del perfil"
            className="h-40 w-40 rounded-3xl object-cover border border-gray-200 shadow-sm"
          />
          <div className="w-full max-w-md space-y-3">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="block w-full text-sm text-gray-700"
            />
            <p className="text-xs text-gray-500">Puedes tomar una foto o elegirla desde Fotos. Máximo {MAX_PROFILE_IMAGE_SIZE_MB}MB.</p>
            <button
              type="button"
              onClick={handleImageUpload}
              disabled={uploading || !imageUpload}
              className="w-full rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            >
              {uploading ? 'Subiendo...' : 'Subir imagen'}
            </button>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Enlace de tu tarjeta</h3>
        <p className="text-sm text-gray-500 mb-4">
          Usa este enlace en la tarjeta para abrir tu perfil publico.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            type="text"
            readOnly
            value={nfcLink}
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
          />
          <button
            type="button"
            onClick={handleCopyLink}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white"
          >
            Copiar link
          </button>
        </div>
        {copyStatus && <p className="mt-2 text-sm text-gray-500">{copyStatus}</p>}
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Información principal</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <input
            type="text"
            name="displayName"
            placeholder="Nombre completo"
            value={form.displayName}
            onChange={handleChange}
            className="rounded-lg border border-gray-300 px-3 py-2"
          />
          <input
            type="text"
            name="title"
            placeholder="Cargo o especialidad"
            value={form.title}
            onChange={handleChange}
            className="rounded-lg border border-gray-300 px-3 py-2"
          />
          <textarea
            name="bio"
            placeholder="Descripción corta"
            value={form.bio}
            onChange={handleChange}
            className="md:col-span-2 rounded-lg border border-gray-300 px-3 py-2"
            rows={3}
          />
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Lo llamativo de nuestros desarrollos y soluciones
        </h3>
        <div className="grid gap-4 md:grid-cols-2">
          <textarea
            name="highlightsText"
            placeholder="Highlights (uno por línea)"
            value={form.highlightsText}
            onChange={handleChange}
            className="rounded-lg border border-gray-300 px-3 py-2"
            rows={5}
          />
          <textarea
            name="solutionsText"
            placeholder="Soluciones que ofreces (una por línea)"
            value={form.solutionsText}
            onChange={handleChange}
            className="rounded-lg border border-gray-300 px-3 py-2"
            rows={5}
          />
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Redes sociales</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <input
            type="text"
            name="whatsapp"
            placeholder="WhatsApp (ej. 17871234567)"
            value={form.whatsapp}
            onChange={handleChange}
            className="rounded-lg border border-gray-300 px-3 py-2"
          />
          <input
            type="text"
            name="instagram"
            placeholder="Instagram (@usuario o URL)"
            value={form.instagram}
            onChange={handleChange}
            className="rounded-lg border border-gray-300 px-3 py-2"
          />
          <input
            type="text"
            name="facebook"
            placeholder="Facebook (usuario o URL)"
            value={form.facebook}
            onChange={handleChange}
            className="rounded-lg border border-gray-300 px-3 py-2"
          />
          <input
            type="text"
            name="linkedin"
            placeholder="LinkedIn (perfil o URL)"
            value={form.linkedin}
            onChange={handleChange}
            className="rounded-lg border border-gray-300 px-3 py-2"
          />
          <input
            type="text"
            name="website"
            placeholder="Website"
            value={form.website}
            onChange={handleChange}
            className="rounded-lg border border-gray-300 px-3 py-2"
          />
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Promociones y trabajos</h3>
          <button
            type="button"
            onClick={addPromotion}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Agregar promoción
          </button>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Añade ejemplos como menús, proyectos o campañas para mostrarlos en tu tarjeta.
        </p>
        <div className="mt-4 space-y-4">
          {promotions.map((promo, index) => (
            <div
              key={`promo-${index}`}
              className="grid gap-3 rounded-xl border border-gray-200 p-4 md:grid-cols-3"
            >
              <input
                type="text"
                placeholder="Titulo (ej. Menu Prosecco)"
                value={promo.title}
                onChange={(event) =>
                  handlePromotionChange(index, 'title', event.target.value)
                }
                className="rounded-lg border border-gray-300 px-3 py-2"
              />
              <input
                type="text"
                placeholder="URL de imagen"
                value={promo.imageUrl}
                onChange={(event) =>
                  handlePromotionChange(index, 'imageUrl', event.target.value)
                }
                className="rounded-lg border border-gray-300 px-3 py-2"
              />
              <input
                type="text"
                placeholder="Link (opcional)"
                value={promo.link}
                onChange={(event) =>
                  handlePromotionChange(index, 'link', event.target.value)
                }
                className="rounded-lg border border-gray-300 px-3 py-2"
              />
              <div className="md:col-span-3 flex flex-col gap-2">
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={(event) =>
                    handlePromotionFile(index, event.target.files?.[0])
                  }
                  className="block w-full text-sm text-gray-700"
                />
                <p className="text-xs text-gray-500">
                  Puedes subir imagen, GIF o video (max {MAX_PROMO_SIZE_MB}MB).
                </p>
                {(promo.mediaUrl || promo.imageUrl) && (
                  <div className="text-xs text-gray-500 break-all">
                    {promo.mediaUrl || promo.imageUrl}
                  </div>
                )}
              </div>
              {promotions.length > 1 && (
                <div className="md:col-span-3">
                  <button
                    type="button"
                    onClick={() => removePromotion(index)}
                    className="text-sm font-medium text-red-600 hover:text-red-700"
                  >
                    Quitar
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-lg bg-orange-500 px-6 py-2 text-white font-medium hover:bg-orange-600 disabled:opacity-60"
        >
          {saving ? 'Guardando...' : 'Guardar perfil'}
        </button>
      </div>
    </div>
  );
};

export default StaffProfile;
